import React from 'react';
import { BookOpen, CalendarDays, FileText } from 'lucide-react';

type ToolId = 'daily' | 'notes' | 'wiki';

function toolButtonStyle(active: boolean, showSidebarLabels: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: showSidebarLabels ? '7px 12px' : '8px 0',
    justifyContent: showSidebarLabels ? 'flex-start' : 'center',
    borderRadius: 9,
    background: active ? 'var(--z-fill-2)' : 'transparent',
    color: 'var(--z-text-2)',
    fontSize: 12.5,
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
  };
}

function ToolIcon({
  children,
  color,
  background,
}: {
  children: React.ReactNode;
  color: string;
  background: string;
}) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 24,
        height: 24,
        flex: '0 0 24px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 7,
        color,
        background,
      }}
    >
      {children}
    </span>
  );
}

function DailyBadge({ dailySent }: { dailySent: boolean }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: '1px 6px',
        borderRadius: 99,
        background: dailySent ? 'var(--z-ok-bg)' : 'var(--z-warn-bg)',
        color: dailySent ? 'var(--z-ok-ink)' : 'var(--z-warn-ink)',
      }}
    >
      {dailySent ? 'Enviado' : 'Pendiente'}
    </span>
  );
}

function CountBadge({ count }: { count: number }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: '1px 6px',
        borderRadius: 99,
        background: 'var(--z-fill-2)',
        color: 'var(--z-dim)',
      }}
    >
      {count}
    </span>
  );
}

export interface SidebarToolsSectionProps {
  showSidebarLabels: boolean;
  dailySent: boolean;
  activeTool: ToolId | null;
  remindersCount?: number;
  notesCount: number;
  onOpenTool: (tool: ToolId) => void;
}

export const SidebarToolsSection: React.FC<SidebarToolsSectionProps> = ({
  showSidebarLabels,
  dailySent,
  activeTool,
  notesCount,
  onOpenTool,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    {showSidebarLabels ? (
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--z-dim)',
          padding: '4px 10px',
        }}
      >
        Herramientas
      </div>
    ) : null}

    <button
      onClick={() => onOpenTool('daily')}
      style={toolButtonStyle(activeTool === 'daily', showSidebarLabels)}
      title="Daily Standup"
    >
      <ToolIcon color="var(--z-accent-2)" background="var(--z-accent-soft)">
        <CalendarDays size={15} strokeWidth={2.2} />
      </ToolIcon>
      {showSidebarLabels ? (
        <>
          <span style={{ flex: '1 1 auto' }}>Daily</span>
          <DailyBadge dailySent={dailySent} />
        </>
      ) : null}
    </button>

    <button
      onClick={() => onOpenTool('notes')}
      style={toolButtonStyle(activeTool === 'notes', showSidebarLabels)}
      title="Notas Rápidas"
    >
      <ToolIcon color="var(--z-ok-ink)" background="var(--z-ok-bg)">
        <FileText size={15} strokeWidth={2.2} />
      </ToolIcon>
      {showSidebarLabels ? (
        <>
          <span style={{ flex: '1 1 auto' }}>Notas</span>
          <CountBadge count={notesCount} />
        </>
      ) : null}
    </button>

    <button
      onClick={() => onOpenTool('wiki')}
      style={toolButtonStyle(activeTool === 'wiki', showSidebarLabels)}
      title="LLM Wiki"
    >
      <ToolIcon color="var(--z-accent-ink)" background="var(--z-accent-soft)">
        <BookOpen size={15} strokeWidth={2.2} />
      </ToolIcon>
      {showSidebarLabels ? <span style={{ flex: '1 1 auto' }}>LLM Wiki</span> : null}
    </button>
  </div>
);
